import { api } from "lib/graphql/api"
import { mutate } from "lib/graphql/mutate"
import { gql } from "@apollo/client"
import { ApplyBusy } from "routes/regime/plugins/zero-u-wizard/apply-all-button"

const updateQueuesByRegimeId = new Map()
const busyCountsByRegimeId = new Map()

export const updateZeroU = api(async ({ regimeId, group, search, filters = [], interval, period, minutes }) => {
    const key = regimeId ?? "__no_regime__"
    const previous = updateQueuesByRegimeId.get(key) ?? Promise.resolve()
    const busyCount = (busyCountsByRegimeId.get(key) ?? 0) + 1
    busyCountsByRegimeId.set(key, busyCount)
    ApplyBusy.raise({ busy: true, count: busyCount })

    const next = previous
        .catch(() => undefined)
        .then(() =>
            mutate(
                gql`
                    mutation UpdateZeroU(
                        $regimeId: String!
                        $group: String
                        $search: String
                        $filters: [JSONObject]
                        $interval: Int!
                        $period: String!
                        $minutes: Float!
                    ) {
                        updateZeroU(
                            regimeId: $regimeId
                            group: $group
                            search: $search
                            filters: $filters
                            interval: $interval
                            minutes: $minutes
                            period: $period
                        )
                    }
                `,
                {
                    regimeId,
                    group,
                    search,
                    interval,
                    period,
                    minutes,
                    filters: [{ "frequency.label": "0U" }, ...filters],
                },
                {
                    returns: "updateZeroU",
                }
            )
        )
        .finally(() => {
            if (updateQueuesByRegimeId.get(key) === next) {
                updateQueuesByRegimeId.delete(key)
            }
            const remaining = (busyCountsByRegimeId.get(key) ?? 1) - 1
            if (remaining <= 0) {
                busyCountsByRegimeId.delete(key)
                ApplyBusy.raise({ busy: false, count: 0 })
            } else {
                busyCountsByRegimeId.set(key, remaining)
                ApplyBusy.raise({ busy: true, count: remaining })
            }
        })

    updateQueuesByRegimeId.set(key, next)
    return await next
})

/**
 * Batch update multiple Zero U tasks in a single server call.
 * Much more efficient than individual updates for Apply All.
 */
export const updateZeroUBatch = api(async ({ regimeId, group, search, updates }) => {
    ApplyBusy.raise({ busy: true, count: updates.length })

    try {
        const result = await mutate(
            gql`
                mutation UpdateZeroUBatch(
                    $regimeId: String!
                    $group: String
                    $search: String
                    $updates: [JSONObject]!
                ) {
                    updateZeroUBatch(regimeId: $regimeId, group: $group, search: $search, updates: $updates)
                }
            `,
            {
                regimeId,
                group,
                search,
                updates,
            },
            {
                returns: "updateZeroUBatch",
            }
        )
        return result
    } finally {
        ApplyBusy.raise({ busy: false, count: 0 })
    }
})
